import { useEffect, useMemo } from 'react'
import { shallowEqual } from 'react-redux'
import { getIntegrationServiceIdsForCategory, IntegrationCategory, IntegrationServiceId } from '../../../../common/integrations'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { useGetMeQuery } from '../../../../store/user/user'
import { syncApi, type CheckpointRequest } from '../../../../store/sync/sync'

const CALENDAR_DIRECT_IDS = getIntegrationServiceIdsForCategory(IntegrationCategory.CALENDAR)

const wealthboxCalendarCheckpointRequest = (accountId: string): CheckpointRequest => ({
  accountId,
  level: 'user',
  connector: IntegrationServiceId.WEALTHBOX,
  direction: 'inbound',
  object: 'calendar',
})

export interface CalendarSyncStates {
  calendarSyncActive: Set<IntegrationServiceId>
  isLoading: boolean
}

export const useCalendarSyncStates = (): CalendarSyncStates => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { data: me, isLoading: isMeLoading } = useGetMeQuery({ accountId })

  const connectedIds = useMemo(() => new Set(me?.integrations?.map((i) => i.serviceId) ?? []), [me])

  const hasDirectCalendar = useMemo(() => CALENDAR_DIRECT_IDS.some((id) => connectedIds.has(id)), [connectedIds])

  useEffect(() => {
    if (!accountId || hasDirectCalendar || !connectedIds.has(IntegrationServiceId.WEALTHBOX)) {
      return
    }

    const sub = dispatch(syncApi.endpoints.getCheckpoint.initiate(wealthboxCalendarCheckpointRequest(accountId)))
    return () => sub.unsubscribe()
  }, [accountId, hasDirectCalendar, connectedIds, dispatch])

  const activeCalendarSyncIntegrationIds = useAppSelector((state) => {
    const active: IntegrationServiceId[] = []

    for (const id of CALENDAR_DIRECT_IDS) {
      if (connectedIds.has(id)) {
        active.push(id)
      }
    }

    if (!hasDirectCalendar && accountId && connectedIds.has(IntegrationServiceId.WEALTHBOX)) {
      if (syncApi.endpoints.getCheckpoint.select(wealthboxCalendarCheckpointRequest(accountId))(state)?.data?.data?.active === true) {
        active.push(IntegrationServiceId.WEALTHBOX)
      }
    }

    if (me?.integrations?.find((i) => i.serviceId === IntegrationServiceId.REDTAIL)?.serviceConfig?.inbound?.calendar?.enabled === true) {
      active.push(IntegrationServiceId.REDTAIL)
    }

    return active
  }, shallowEqual)

  const isWealthboxCheckpointPending = useAppSelector((state) => {
    if (!accountId || hasDirectCalendar || !connectedIds.has(IntegrationServiceId.WEALTHBOX)) {
      return false
    }
    return syncApi.endpoints.getCheckpoint.select(wealthboxCalendarCheckpointRequest(accountId))(state)?.status === 'pending'
  })

  const calendarSyncActive = useMemo(() => new Set(activeCalendarSyncIntegrationIds), [activeCalendarSyncIntegrationIds])
  const isLoading = isMeLoading || isWealthboxCheckpointPending

  return useMemo(() => ({ calendarSyncActive, isLoading }), [calendarSyncActive, isLoading])
}
